import React, { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../Utils/ExpressAPI";

/**
 * Verification d'un dossier, GREFFEE dans l'ecran « Secretariat (dossier) ».
 *
 * Retour du 28/08 : « j'aime l'idee de verification et de validation des
 * elements cependant un ecran en plus me parait lourd, il faudrait le greffer
 * a l'interface de secretariat. Si tout est ok alors rien n'est apparent, par
 * contre s'il y a une erreur alors on a un lisere de verification qui
 * s'affiche et on doit agir. »
 *
 * D'ou les trois regles de ce composant :
 *   1. dossier propre  -> il ne rend RIEN. Pas un bandeau vert, rien ;
 *   2. dossier en ecart -> un lisere rouge et une phrase, a la premiere
 *      personne : Lybo expose l'ecart et demande a l'humain de trancher ;
 *   3. deux etats seulement par champ, conforme ou a verifier. L'echelle a
 *      quatre niveaux reste en base pour le tri et la mesure, elle ne
 *      s'affiche plus.
 *
 * Chaque correction passe par l'API de validation : la proposition du moteur
 * n'est jamais ecrasee, et le journal garde l'avant et l'apres. C'est ce qui
 * alimentera l'apprentissage et les tableaux de bord de qualite de lecture.
 */

// Le jeton est pose par l'intercepteur de axiosInstance (Utils/ExpressAPI.js).
const entetes = () => ({});

/** Une valeur extraite peut etre un objet : on ne l'affiche pas en [object Object]. */
function affichable(valeur) {
  if (valeur == null) return "";
  if (typeof valeur === "object") {
    return valeur.libelle || valeur.codeNABM || JSON.stringify(valeur);
  }
  return String(valeur);
}

const LIBELLE_CHAMP = {
  nomNaissance: "Nom de naissance",
  nomUsage: "Nom d'usage",
  prenom: "Prénom",
  dateNaissance: "Date de naissance",
  sexe: "Sexe",
  nir: "Numéro de sécurité sociale",
};

function nommerChamp(champ) {
  if (LIBELLE_CHAMP[champ]) return LIBELLE_CHAMP[champ];
  if (champ?.startsWith("analyse")) return "Analyse";
  return champ;
}

/** Les dates arrivent en Timestamp Firestore, en ISO, ou deja en Date. */
function enDate(valeur) {
  if (!valeur) return null;
  if (valeur._seconds) return new Date(valeur._seconds * 1000);
  if (valeur.seconds) return new Date(valeur.seconds * 1000);
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

function horodate(valeur) {
  const d = enDate(valeur);
  return d ? d.toLocaleString("fr-FR") : "";
}

export default function VerificationDossier({ appointmentId, onTravail }) {
  const [dossier, setDossier] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [brouillon, setBrouillon] = useState({});
  const [enregistrement, setEnregistrement] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    if (!appointmentId) {
      setDossier(null);
      return;
    }
    try {
      const reponse = await axiosInstance.get(
        `dossiers/par-rendez-vous/${appointmentId}`,
        { headers: entetes() }
      );
      // 204 : ce rendez-vous n'a pas de dossier documentaire. Ce n'est pas une
      // erreur, c'est le cas de tous ceux d'avant la mise en place du modele.
      setDossier(reponse.status === 204 ? null : reponse.data);
    } catch (e) {
      // Un service indisponible ne doit pas abimer l'ecran du secretariat :
      // on se tait, le reste du dossier fonctionne.
      setDossier(null);
    }
  }, [appointmentId]);

  useEffect(() => {
    setOuvert(false);
    setBrouillon({});
    setErreur(null);
    charger();
  }, [charger]);

  if (!dossier || !dossier.exception?.estUneException) return null;

  const extractions = dossier.extractions || [];
  const aVerifier = extractions.filter((e) => e.conformite !== "conforme");
  // Les phrases de Lybo si on en a ; sinon la raison technique, qui reste
  // preferable a un bandeau muet.
  const phrases = dossier.messagesVerification?.length
    ? dossier.messagesVerification
    : [dossier.dossier?.raisonQualification].filter(Boolean);

  const valider = async (extraction) => {
    const valeur =
      brouillon[extraction.id] ??
      affichable(
        extraction.valeurValidee ?? extraction.valeurNormalisee ?? extraction.valeurProposee
      );
    setEnregistrement(extraction.id);
    setErreur(null);
    try {
      await axiosInstance.post(
        `dossiers/extractions/${extraction.id}/valider`,
        { valeurValidee: valeur },
        { headers: entetes() }
      );
      if (onTravail) onTravail();
      // On recharge : le journal doit montrer l'evenement qui vient d'etre ecrit.
      await charger();
    } catch (e) {
      setErreur(
        e?.response?.data?.error || "La validation n'a pas pu être enregistrée."
      );
    } finally {
      setEnregistrement(null);
    }
  };

  return (
    <div className="sb-card">
      <div className="sb-verifBanner" onClick={() => setOuvert((v) => !v)}>
        <span style={{ fontSize: 18 }}>⚠</span>
        <div style={{ flex: 1 }}>
          <div className="sb-verifBannerTitle">
            {dossier.exception.libelle} — {aVerifier.length || dossier.exception.motifs.length} point
            {(aVerifier.length || dossier.exception.motifs.length) > 1 ? "s" : ""} à vérifier
          </div>
          {phrases.map((p) => (
            <div className="sb-verifBannerText" key={p}>
              {p}
            </div>
          ))}
        </div>
        <span className="sb-btnMini">{ouvert ? "Fermer" : "Vérifier"}</span>
      </div>

      {ouvert && (
        <div className="sb-verifPanel">
          {extractions.length === 0 && (
            <div className="sb-pill">Aucune donnée extraite pour ce dossier.</div>
          )}

          {extractions.map((e) => {
            const courante =
              brouillon[e.id] ??
              affichable(e.valeurValidee ?? e.valeurNormalisee ?? e.valeurProposee);
            const conforme = e.conformite === "conforme";
            return (
              <div className="sb-verifRow" key={e.id}>
                <div className="sb-verifChamp">{nommerChamp(e.champ)}</div>
                <div style={{ flex: 1 }}>
                  <input
                    className="sb-verifInput"
                    value={courante}
                    onChange={(ev) =>
                      setBrouillon({ ...brouillon, [e.id]: ev.target.value })
                    }
                  />
                  {/* Ce que le moteur a reellement lu reste visible : c'est ce
                      qui permet de trancher sans rouvrir la photo. */}
                  {e.texteBrut && (
                    <div className="sb-verifLu">lu : « {e.texteBrut} »</div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 4 }}>
                  <span className={`sb-verifEtat ${conforme ? "conforme" : "nonconforme"}`}>
                    {conforme ? "Conforme" : "À vérifier"}
                  </span>
                  {e.valeurValidee != null ? (
                    <span style={{ color: "var(--ok)", fontWeight: 900 }}>✓</span>
                  ) : (
                    <button
                      className="sb-btnMini sb-blue"
                      disabled={enregistrement === e.id}
                      onClick={() => valider(e)}
                    >
                      {enregistrement === e.id ? "…" : "Valider"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {erreur && (
            <div className="sb-pill sb-warn" style={{ marginTop: 8 }}>
              {erreur}
            </div>
          )}

          {/* Tracabilite : qui a change quoi, quand, et ce qu'il y avait avant.
              C'est aussi la matiere des tableaux de bord de qualite de lecture. */}
          <div className="sb-verifJournal">
            <div className="sb-k" style={{ marginBottom: 4 }}>
              Traçabilité
            </div>
            {(dossier.journal || []).length === 0 && (
              <div className="sb-verifEvent">Aucun événement.</div>
            )}
            {(dossier.journal || []).map((ev) => (
              <div className="sb-verifEvent" key={ev.id}>
                <strong>{ev.action}</strong> — {ev.auteur} · {horodate(ev.horodatage)}
                {ev.valeurAvant && ev.valeurApres && (
                  <div style={{ color: "var(--muted)" }}>
                    {affichable(ev.valeurAvant.normalisee ?? ev.valeurAvant.proposee ?? ev.valeurAvant.statut)}
                    {" → "}
                    {affichable(ev.valeurApres.validee ?? ev.valeurApres.statut)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
